import api from "./api";

/** Form values -> the payload the API expects. */
const toPayload = (values) => {
  const payload = {
    name: values.name,
    email: values.email,
    // A role of this business, chosen from /business/roles.
    role_id: values.roleId || undefined,
    is_active: Boolean(values.isActive),
  };

  // Omitted, not emptied. The controller keys on array_key_exists('branch_ids'),
  // so an absent key leaves the user's branches alone while an empty array
  // clears them — and clearing them for an owner would drop manager_id off
  // every branch they run. The staff form sends undefined when the section is
  // not shown, which is exactly the owner case.
  if (values.branchIds !== undefined) {
    payload.branch_ids = values.branchIds;
  }

  // An empty password on edit means "leave it alone".
  if (values.password) {
    payload.password = values.password;
    payload.password_confirmation = values.passwordConfirmation;
  }

  return payload;
};

/**
 * GET /business/users
 * `filters` accepts { search, roleId, isActive }; the tri-state ones are left
 * out entirely when "" — false is a real value the API tells from "no filter".
 */
export const fetchUsers = async ({ page = 1, perPage = 15, filters = {} } = {}) => {
  const params = { page, per_page: perPage };

  if (filters.search) params.search = filters.search;
  if (filters.roleId) params.role_id = filters.roleId;
  if (filters.isActive !== "" && filters.isActive != null) params.is_active = filters.isActive;

  const { data } = await api.get("/business/users", { params });
  return { users: data.data.users, meta: data.meta };
};

export const fetchUser = async (id) => {
  const { data } = await api.get(`/business/users/${id}`);
  return data.data.user;
};

export const createUser = async (values) => {
  const { data } = await api.post("/business/users", {
    ...toPayload(values),
    password: values.password,
    password_confirmation: values.passwordConfirmation,
  });
  return data.data.user;
};

export const updateUser = async (id, values) => {
  const { data } = await api.put(`/business/users/${id}`, toPayload(values));
  return data.data.user;
};

export const deleteUser = (id) => api.delete(`/business/users/${id}`);

/**
 * PUT /business/users/{id}/branches — replaces the user's whole set. Branches
 * dropped go unassigned; branches added are taken from their previous manager,
 * since a branch has exactly one.
 */
export const syncManagedBranches = async (id, branchIds) => {
  const { data } = await api.put(`/business/users/${id}/branches`, { branch_ids: branchIds });
  return data.data.user;
};

/* ---------------------------------------------------------- branch manager */

/** PUT /business/branches/{id}/manager — an existing user, or a new one. */
export const assignBranchManager = async (branchId, values) => {
  const body =
    values.managerMode === "new"
      ? {
          manager_mode: "new",
          new_manager: {
            name: values.newManager.name,
            email: values.newManager.email,
            password: values.newManager.password,
            password_confirmation: values.newManager.passwordConfirmation,
          },
        }
      : { manager_mode: "existing", user_id: values.userId };

  const { data } = await api.put(`/business/branches/${branchId}/manager`, body);
  return data.data.branch;
};

/** DELETE /business/branches/{id}/manager — leave the branch unassigned. */
export const clearBranchManager = async (branchId) => {
  const { data } = await api.delete(`/business/branches/${branchId}/manager`);
  return data.data.branch;
};
